export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return date.toLocaleDateString()
}

export function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
}

export function truncate(str: string, len: number = 100): string {
  if (str.length <= len) return str
  return str.slice(0, len) + '...'
}

export function getProviderColor(provider: string): string {
  switch (provider) {
    case 'openai': return '#10a37f'
    case 'anthropic': return '#d4a574'
    case 'xai': return '#1da1f2'
    case 'deepseek': return '#4e8cff'
    case 'google': return '#4285f4'
    case 'mistral': return '#ff7000'
    case 'qwen': return '#6c5ce7'
    case 'llama': return '#0467df'
    case 'deepinfra': return '#e91e63'
    case 'kimi': return '#00c9a7'
    default: return '#58a6ff'
  }
}

// Marketing name mapping: model ID → { name: marketing name, provider: provider label }
const MODEL_NAMES: Record<string, { name: string; provider: string }> = {
  // OpenAI
  'gpt-4o': { name: 'ChatGPT 4o', provider: 'OpenAI' },
  'gpt-4o-mini': { name: 'ChatGPT 4o Mini', provider: 'OpenAI' },
  'gpt-4.1': { name: 'ChatGPT 4.1', provider: 'OpenAI' },
  'gpt-4.1-mini': { name: 'ChatGPT 4.1 Mini', provider: 'OpenAI' },
  'gpt-4.1-nano': { name: 'ChatGPT 4.1 Nano', provider: 'OpenAI' },
  'gpt-4.5-preview': { name: 'ChatGPT 4.5 Preview', provider: 'OpenAI' },
  'o3': { name: 'o3 Reasoning', provider: 'OpenAI' },
  'o4-mini': { name: 'o4 Mini', provider: 'OpenAI' },
  'gpt-3.5-turbo': { name: 'ChatGPT 3.5 Turbo', provider: 'OpenAI' },
  // Anthropic
  'claude-opus-4-6': { name: 'Claude Opus 4.6', provider: 'Anthropic' },
  'claude-sonnet-4-6': { name: 'Claude Sonnet 4.6', provider: 'Anthropic' },
  'claude-haiku-4-5-20251001': { name: 'Claude Haiku 4.5', provider: 'Anthropic' },
  // Google
  'gemini-2.5-pro': { name: 'Gemini 2.5 Pro', provider: 'Google' },
  'gemini-2.5-flash': { name: 'Gemini 2.5 Flash', provider: 'Google' },
  'gemini-2.0-flash': { name: 'Gemini 2.0 Flash', provider: 'Google' },
  'gemini-2.0-flash-lite': { name: 'Gemini 2.0 Flash Lite', provider: 'Google' },
  // xAI
  'grok-4': { name: 'Grok 4', provider: 'xAI' },
  'grok-3': { name: 'Grok 3', provider: 'xAI' },
  'grok-3-fast': { name: 'Grok 3 Fast', provider: 'xAI' },
  'grok-3-mini': { name: 'Grok 3 Mini', provider: 'xAI' },
  'grok-3-mini-fast': { name: 'Grok 3 Mini Fast', provider: 'xAI' },
  // DeepSeek
  'deepseek-chat': { name: 'DeepSeek V3', provider: 'DeepSeek' },
  'deepseek-reasoner': { name: 'DeepSeek R1', provider: 'DeepSeek' },
  // Mistral
  'mistral-large-latest': { name: 'Mistral Large', provider: 'Mistral AI' },
  'mistral-medium-latest': { name: 'Mistral Medium', provider: 'Mistral AI' },
  'mistral-small-latest': { name: 'Mistral Small', provider: 'Mistral AI' },
  'codestral-latest': { name: 'Codestral', provider: 'Mistral AI' },
  'pixtral-large-latest': { name: 'Pixtral Large', provider: 'Mistral AI' },
  'ministral-8b-latest': { name: 'Ministral 8B', provider: 'Mistral AI' },
  // Meta
  'llama-4-maverick': { name: 'Llama 4 Maverick', provider: 'Meta' },
  'llama-4-scout': { name: 'Llama 4 Scout', provider: 'Meta' },
  'llama-3.3-70b': { name: 'Llama 3.3 70B', provider: 'Meta' },
  'llama-3.1-405b': { name: 'Llama 3.1 405B', provider: 'Meta' },
  // Amazon
  'amazon.nova-pro-v1': { name: 'Nova Pro', provider: 'Amazon' },
  'amazon.nova-lite-v1': { name: 'Nova Lite', provider: 'Amazon' },
  'amazon.nova-micro-v1': { name: 'Nova Micro', provider: 'Amazon' },
  // Qwen
  'qwen-max': { name: 'Qwen Max', provider: 'Alibaba' },
  'qwen-plus': { name: 'Qwen Plus', provider: 'Alibaba' },
  'qwen-turbo': { name: 'Qwen Turbo', provider: 'Alibaba' },
  'qwen-long': { name: 'Qwen Long', provider: 'Alibaba' },
  'qwen-vl-max': { name: 'Qwen VL Max', provider: 'Alibaba' },
  'qwen-coder-turbo': { name: 'Qwen Coder Turbo', provider: 'Alibaba' },
  // Baidu
  'ernie-4.5': { name: 'ERNIE 4.5', provider: 'Baidu' },
  'ernie-4.0-turbo': { name: 'ERNIE 4.0 Turbo', provider: 'Baidu' },
  'ernie-3.5': { name: 'ERNIE 3.5', provider: 'Baidu' },
  // Zhipu
  'glm-4-plus': { name: 'GLM 4 Plus', provider: 'Zhipu AI' },
  'glm-4': { name: 'GLM 4', provider: 'Zhipu AI' },
  'glm-4-flash': { name: 'GLM 4 Flash', provider: 'Zhipu AI' },
  'glm-4v-plus': { name: 'GLM 4V Plus', provider: 'Zhipu AI' },
  // Kimi
  'moonshot-v1-128k': { name: 'Kimi 128K', provider: 'Moonshot' },
  'moonshot-v1-32k': { name: 'Kimi 32K', provider: 'Moonshot' },
  'moonshot-v1-8k': { name: 'Kimi 8K', provider: 'Moonshot' },
  // Together AI (hosted models)
  'meta-llama/Llama-3.3-70B-Instruct-Turbo': { name: 'Llama 3.3 70B Turbo', provider: 'Together AI' },
  'meta-llama/Llama-4-Maverick-17B-128E-Instruct-FP8': { name: 'Llama 4 Maverick', provider: 'Together AI' },
  'Qwen/Qwen3-235B-A22B': { name: 'Qwen 3 235B', provider: 'Together AI' },
  'deepseek-ai/DeepSeek-R1': { name: 'DeepSeek R1', provider: 'Together AI' },
  // Groq
  'llama-3.3-70b-versatile': { name: 'Llama 3.3 70B', provider: 'Groq' },
  'llama-3.1-8b-instant': { name: 'Llama 3.1 8B', provider: 'Groq' },
  'gemma2-9b-it': { name: 'Gemma 2 9B', provider: 'Groq' },
  'mixtral-8x7b-32768': { name: 'Mixtral 8x7B', provider: 'Groq' },
  // Cohere
  'command-r-plus': { name: 'Command R+', provider: 'Cohere' },
  'command-r': { name: 'Command R', provider: 'Cohere' },
  'command-a': { name: 'Command A', provider: 'Cohere' },
  'command-light': { name: 'Command Light', provider: 'Cohere' },
  // Perplexity
  'sonar-pro': { name: 'Sonar Pro', provider: 'Perplexity' },
  'sonar': { name: 'Sonar', provider: 'Perplexity' },
  'sonar-deep-research': { name: 'Sonar Deep Research', provider: 'Perplexity' },
  'sonar-reasoning-pro': { name: 'Sonar Reasoning Pro', provider: 'Perplexity' },
  // AI21
  'jamba-1.5-large': { name: 'Jamba 1.5 Large', provider: 'AI21' },
  'jamba-1.5-mini': { name: 'Jamba 1.5 Mini', provider: 'AI21' },
  'jamba-instruct': { name: 'Jamba Instruct', provider: 'AI21' },
  // Yi
  'yi-lightning': { name: 'Yi Lightning', provider: '01.AI' },
  'yi-large': { name: 'Yi Large', provider: '01.AI' },
  'yi-large-turbo': { name: 'Yi Large Turbo', provider: '01.AI' },
  'yi-medium': { name: 'Yi Medium', provider: '01.AI' },
  // OpenRouter
  'openai/gpt-4o': { name: 'ChatGPT 4o', provider: 'OpenRouter' },
  'anthropic/claude-sonnet-4-6': { name: 'Claude Sonnet 4.6', provider: 'OpenRouter' },
  'google/gemini-2.5-pro': { name: 'Gemini 2.5 Pro', provider: 'OpenRouter' },
  'meta-llama/llama-3.3-70b-instruct': { name: 'Llama 3.3 70B', provider: 'OpenRouter' },
  'deepseek/deepseek-chat': { name: 'DeepSeek V3', provider: 'OpenRouter' },
  // Stability AI
  'stable-diffusion-3.5-large': { name: 'Stable Diffusion 3.5', provider: 'Stability AI' },
  'stable-image-ultra': { name: 'Stable Image Ultra', provider: 'Stability AI' },
  'stable-image-core': { name: 'Stable Image Core', provider: 'Stability AI' },
  // Runway
  'gen-4': { name: 'Gen-4', provider: 'Runway' },
  'gen-3-alpha': { name: 'Gen-3 Alpha', provider: 'Runway' },
  // ElevenLabs
  'eleven_multilingual_v2': { name: 'Multilingual V2', provider: 'ElevenLabs' },
  'eleven_turbo_v2.5': { name: 'Turbo V2.5', provider: 'ElevenLabs' },
  // Suno
  'suno-v4': { name: 'Suno V4', provider: 'Suno' },
  'chirp-v3': { name: 'Chirp V3', provider: 'Suno' },
}

export function getModelDisplayName(model: string): string {
  const entry = MODEL_NAMES[model]
  if (entry) return `${entry.name} (${model})`
  return model
}

export function getModelShortName(model: string): string {
  const entry = MODEL_NAMES[model]
  if (entry) return entry.name
  // Fallback for unknown models
  if (model.includes('gpt-4o')) return 'ChatGPT 4o'
  if (model.includes('gpt-4')) return 'ChatGPT 4'
  if (model.includes('gpt-3.5')) return 'ChatGPT 3.5'
  if (model.includes('opus')) return 'Claude Opus'
  if (model.includes('sonnet')) return 'Claude Sonnet'
  if (model.includes('haiku')) return 'Claude Haiku'
  if (model.includes('grok')) return 'Grok'
  if (model.includes('deepseek')) return 'DeepSeek'
  if (model.includes('gemini')) return 'Gemini'
  if (model.includes('mistral')) return 'Mistral'
  if (model.includes('qwen')) return 'Qwen'
  if (model.includes('llama') || model.includes('Llama')) return 'Llama'
  if (model.includes('moonshot')) return 'Kimi'
  return model
}
